import { Controller } from "@hotwired/stimulus"

/**
 * Clipboard Controller
 *
 * Copies text to clipboard when element is clicked or Enter/Space is pressed.
 * Shows visual feedback by temporarily changing the icon.
 *
 * Usage:
 *   <button data-controller="clipboard"
 *           data-clipboard-text-value="text to copy"
 *           data-action="click->clipboard#copy keydown->clipboard#handleKeydown">
 *     <span data-clipboard-target="text">text to copy</span>
 *     <i data-clipboard-target="icon" class="fas fa-copy"></i>
 *   </button>
 */
export default class extends Controller {
  static targets = ["text", "icon"]
  static values = {
    text: String,
    successDuration: { type: Number, default: 2000 }
  }

  /**
   * Copy text to clipboard
   */
  async copy(event) {
    event.preventDefault()

    const textToCopy = this.textValue || this.textTarget?.textContent?.trim()

    if (!textToCopy) {
      console.warn("Clipboard: No text to copy")
      return
    }

    try {
      await navigator.clipboard.writeText(textToCopy)
      this.showSuccess()
    } catch (err) {
      // Fallback for older browsers or restricted contexts
      this.fallbackCopy(textToCopy)
    }
  }

  /**
   * Handle keyboard navigation (Enter or Space to activate)
   */
  handleKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.copy(event)
    }
  }

  /**
   * Fallback copy method for browsers without clipboard API
   */
  fallbackCopy(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand("copy")
      this.showSuccess()
    } catch (err) {
      console.error("Clipboard: Failed to copy", err)
    } finally {
      document.body.removeChild(textarea)
    }
  }

  /**
   * Show success feedback by changing the icon
   */
  showSuccess() {
    if (!this.hasIconTarget) return

    const icon = this.iconTarget

    // Store original classes
    const originalClasses = icon.className

    // Change to checkmark
    icon.className = "fas fa-check text-success"

    // Revert after duration
    setTimeout(() => {
      icon.className = originalClasses
    }, this.successDurationValue)
  }
}
